import { motion } from "motion/react";
import { ChevronRight, Layers } from "lucide-react";
import { cn } from "@/lib/utils";
import { subjectAccent, subjectTint } from "@/lib/app-types";
import { chapterStats, type StudyChapter } from "@/lib/lecture-plan";

export function ChapterCard({
  chapter,
  today,
  index,
  onOpen,
}: {
  chapter: StudyChapter;
  today: string;
  index: number;
  onOpen: (chapter: StudyChapter) => void;
}) {
  const stats = chapterStats(chapter, today);
  const dueCount = stats.dueToday + stats.overdue;

  return (
    <motion.button
      type="button"
      onClick={() => onOpen(chapter)}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      whileTap={{ scale: 0.985 }}
      transition={{
        delay: Math.min(index * 0.04, 0.2),
        type: "spring",
        stiffness: 260,
        damping: 26,
      }}
      className="glass w-full rounded-[24px] p-5 text-left"
    >
      <div className="flex items-start gap-3">
        <span
          className={cn("mt-1.5 size-2.5 shrink-0 rounded-full bg-gradient-to-br", subjectAccent(chapter.subject))}
        />
        <div className="min-w-0 flex-1">
          <p className={cn("text-[11px] font-semibold uppercase tracking-wider", subjectTint(chapter.subject))}>
            {chapter.subject}
          </p>
          <h3 className="truncate text-[16px] font-bold tracking-tight">{chapter.chapter}</h3>
        </div>
        <span className="shrink-0 rounded-full border border-hairline bg-surface px-2.5 py-1 text-[11px] font-semibold text-muted-foreground">
          Tap to view
        </span>
      </div>

      <div className="mt-4 flex items-center gap-3">
        <div className="h-2 flex-1 overflow-hidden rounded-full bg-surface">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${stats.percent}%` }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            className="h-full rounded-full [background-image:var(--gradient-brand)]"
          />
        </div>
        <span className="text-[12px] font-bold tabular-nums">
          {stats.completed}/{chapter.totalLectures}
        </span>
      </div>

      <div className="mt-3 flex items-center justify-between gap-3 text-[11px] text-muted-foreground">
        <span className="flex min-w-0 items-center gap-1.5">
          <Layers className="size-3.5 shrink-0 text-primary" />
          <span className="truncate">
            {dueCount > 0
              ? `${dueCount} revision${dueCount === 1 ? "" : "s"} due`
              : `${stats.remaining} lecture${stats.remaining === 1 ? "" : "s"} remaining`}
          </span>
        </span>
        <span className="flex shrink-0 items-center gap-1.5">
          {chapter.totalLectures - stats.remaining}/{chapter.totalLectures} done
          <ChevronRight className="size-3.5" />
        </span>
      </div>
    </motion.button>
  );
}
